import { buildPanelCheckItems } from 'components/Cruises/SearchPage/Filters/Inputs/buildPanelCheckItems'
import FilterPanelCheckboxGroup, { type FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import CruiseLuxPlusFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseLuxPlusFilter'
import CruiseLuxPlusPriceFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseLuxPlusPriceFilter'
import type {
  CruisePriceFilterChangeProps,
} from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruisePriceFilter'
import CruisePriceFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruisePriceFilter'
import CruisePromotionFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruisePromotionFilter'
import CruiseShipFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseShipFilter'
import CruiseCruiseTypeFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseCruiseTypeFilter'
import CruiseDurationFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseDurationFilter'
import CruiseTravellerTypeFilter from 'components/Cruises/SearchPage/Filters/Inputs/Common/CruiseSearchExtraFilter/CruiseTravellerTypeFilter'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import type { CRUISE_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import useCruiseFacetFiltersParams from 'hooks/useCruiseFacetFiltersParams'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { semiFormToObject } from 'lib/forms/formUtils'
import noop from 'lib/function/noop'
import debounce from 'lodash/debounce'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusEnabled, getIsEnabledLuxPlusDiscountedCruise } from 'luxPlus/selectors/featureToggle'
import { queryKeyCampaigns } from 'constants/url'
import { triplePointCampaignId, triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeover.constants'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import useLoyaltyFilterLabels from 'hooks/Search/useLoyaltyFilterLabels'
import LoyaltySocietePointsFilterGroup from 'loyalty/components/LoyaltySocietePointsFilterGroup'
import FilterPanelLoader from 'components/Common/FilterPanel/FilterPanelCheckboxLoader'
export function getCruiseSearchFormValues(formValues: Record<string, any>): Record<string, any> {
  delete formValues.price

  if (formValues.isLuxExclusivePromotion) {
    formValues.isLuxExclusivePromotion = true
  }

  if (formValues.isSpecialOffers) {
    formValues.isSpecialOffers = true
  }

  if (formValues.isLuxPlus) {
    formValues.isLuxPlus = true
  }

  if (formValues.hasLuxPlusPrice) {
    formValues.hasLuxPlusPrice = true
  }

  // Normalise campaigns: only array or string (comma-separated) are supported from the form
  if (typeof formValues.campaigns === 'string') {
    formValues.campaigns = formValues.campaigns.split(',').filter(Boolean)
  } else if (formValues.campaigns != null && !Array.isArray(formValues.campaigns)) {
    formValues.campaigns = undefined
  }
  // there is no true 'loyalty' filter (yet), always clear it
  formValues.loyalty = undefined

  return formValues
}

interface Props {
  listFilters: App.OfferListFilters
  onChange?: (values: Record<string, any>) => void
  shouldHidePromotionFilters?: boolean
  shouldHideCruiseShips?: boolean
}

function CruiseSearchFiltersInputs(props: Props) {
  const intl = useIntl()
  const { getLabel: getCampaignFilterLabel } = useLoyaltyFilterLabels()
  const {
    listFilters,
    onChange = noop,
    shouldHidePromotionFilters = false,
    shouldHideCruiseShips = false,
  } = props

  const containerRef = useRef<HTMLDivElement | null>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const cruisePriceByNight = useCruisePriceByNight()
  const { facetParams, queryParamsFilters: filters, mappedFilters } = useCruiseFacetFiltersParams({ filters: listFilters })
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const { durationRange } = useContext(GlobalSearchStateContext)

  const isLuxPlusEnabled = useAppSelector(state => getIsLuxPlusEnabled(state))
  const isLuxPlusDiscountedCruise = useAppSelector(state => getIsEnabledLuxPlusDiscountedCruise(state))
  const currentTakeover = useCurrentSiteTakeover({ vertical: 'cruise' })

  const priceFilterTitle = useMemo(
    () =>
      cruisePriceByNight
        ? intl.formatMessage({
            id: 'Rrn5c7xUpvI3QXdsjN5B',
            defaultMessage: 'Price per night',
            description: 'Title for the price filter group in cruise search when per-night mode is selected',
          })
        : intl.formatMessage({
            id: 'xQZtprOe0VAZoG-9XUlN',
            defaultMessage: 'Price per person',
            description: 'Title for the price filter group in cruise search when full price mode is selected',
          }),
    [cruisePriceByNight, intl],
  )

  const [facets, fetchingFacets] = useCruiseSearchFacets(facetParams)
  const getFormValues = useCallback(() => {
    const formValues = semiFormToObject<Record<string, any>>(containerRef.current!)
    return getCruiseSearchFormValues(formValues)
  }, [])

  const onFilterChange = useCallback(() => {
    const formValues = getFormValues()
    onChange(formValues)
  }, [onChange, getFormValues])

  const onResetPrices = useCallback(() => {
    const formValues = getFormValues()
    onChange({ ...formValues, minPrice: undefined, maxPrice: undefined })
  }, [onChange, getFormValues])

  const onDelayedFilterChange = useMemo(() => debounce(onFilterChange, 500), [onFilterChange])

  const onPriceFilterChange = useCallback((priceFilters: CruisePriceFilterChangeProps) => {
    const formValues = getFormValues()
    onChange({ ...formValues, ...priceFilters })
  }, [getFormValues, onChange])

  const onDurationFilterChange = useCallback((nextDurationRange: Array<CRUISE_FLEXIBLE_DURATION_RANGE>) => {
    const formValues = getFormValues()
    onChange({ ...formValues, durationRange: nextDurationRange })
  }, [getFormValues, onChange])

  const onResetDuration = useCallback(() => {
    searchDispatch({
      type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION_RANGE,
      durationRange: [],
    })
    const formValues = getFormValues()
    onChange({ ...formValues, durationRange: undefined })
  }, [getFormValues, onChange, searchDispatch])

  const hasLuxPlusInclusion = useMemo(() => {
    return isLuxPlusEnabled && facets.luxPlus.some(item => item.count > 0)
  }, [facets, isLuxPlusEnabled])

  const hasLuxPlusPrice = useMemo(() => {
    if (!isLuxPlusEnabled || !isLuxPlusDiscountedCruise) return false
    return facets.luxPlusPrice.some(item => item.count > 0)
  }, [facets, isLuxPlusEnabled, isLuxPlusDiscountedCruise])

  const campaignItems = useMemo<Array<FilterPanelCheckItem>>(() => {
    const isTakeoverActive = currentTakeover?.id === triplePointsTakeoverId

    if (!isTakeoverActive) {
      return []
    }

    const triplePointsCampaignFacet = facets.campaigns.find(facet => facet.slug === triplePointCampaignId)

    return buildPanelCheckItems([{
      slug: triplePointCampaignId,
      label: getCampaignFilterLabel(triplePointCampaignId),
      count: triplePointsCampaignFacet?.count ?? 0,
    }]).map(item => ({
      ...item,
      checked: !!filters.campaigns?.has(triplePointCampaignId),
    }))
  }, [currentTakeover?.id, facets.campaigns, filters.campaigns, getCampaignFilterLabel])

  return <VerticalSpacer gap={24} ref={containerRef}>
    {fetchingFacets && <>
      <FilterPanelLoader type="checkbox" />
      <FilterPanelLoader type="checkbox" />
      {isMobile && <FilterPanelLoader type="checkbox" />}
    </>}

    {(!fetchingFacets || isMobile) && <>
      <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: '6QyUfeHkL-oEbSgtUBsm',
          defaultMessage: 'Duration',
          description: 'Title for the cruise duration filter group in cruise search side filters',
        })}
        canReset={!!durationRange?.filter(Boolean).length}
        onReset={onResetDuration}
      >
        <CruiseDurationFilter
          durationFacets={facets.cruiseDurations}
          onChange={onDurationFilterChange}
        />
      </FilterPanelFilterGroup>

      {(hasLuxPlusInclusion || hasLuxPlusPrice) && <FilterPanelFilterGroup
        title={<LuxPlusLabel type="only-logo" />}
        onReset={onFilterChange}
      >
        {hasLuxPlusInclusion && <CruiseLuxPlusFilter
          isLuxPlus={filters.isLuxPlus}
          onChange={onDelayedFilterChange}
          luxPlusFacets={facets.luxPlus}
        />}
        {hasLuxPlusPrice && <CruiseLuxPlusPriceFilter
          luxPlusPriceFacets={facets.luxPlusPrice}
          hasLuxPlusPrice={filters.hasLuxPlusPrice}
          onChange={onDelayedFilterChange}
        />}
      </FilterPanelFilterGroup>}

      {campaignItems.length > 0 && <LoyaltySocietePointsFilterGroup
        viewPriceInPoints={false}
        onViewPriceInPointsChange={noop}
      >
        <FilterPanelCheckboxGroup
          name={queryKeyCampaigns}
          items={campaignItems}
          onChange={onFilterChange}
        />
      </LoyaltySocietePointsFilterGroup>}

      {!shouldHidePromotionFilters && <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'DNdCBIQ7a4ugvGygaFXh',
          defaultMessage: 'Promotions',
          description: 'Title for the promotions filter group in cruise search',
        })}
        canReset={!!filters.promotions?.size}
        onReset={onFilterChange}
      >
        <CruisePromotionFilter
          luxExclusiveFacets={facets.luxExclusive}
          specialOffersFacets={facets.specialOffers}
          isLuxExclusivePromotion={filters.isLuxExclusivePromotion}
          isSpecialOffers={filters.isSpecialOffers}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>}

      <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'Q1KZ-ZjKHYqeE8Y3m8CM',
          defaultMessage: 'Cruise type',
          description: 'Title for the cruise type filter group in cruise search',
        })}
        canReset={!!filters.cruiseTypes?.size}
        onReset={onFilterChange}
      >
        <CruiseCruiseTypeFilter
          cruiseTypes={filters.cruiseTypes}
          cruiseTypeFacets={facets.cruiseTypes}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>

      <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'xjtRC7NtAJO3BnUyvmHf',
          defaultMessage: 'Traveller type',
          description: 'Title for the traveller type filter group in cruise search',
        })}
        canReset={!!filters.travellerTypes?.size}
        onReset={onFilterChange}
      >
        <CruiseTravellerTypeFilter
          travellerTypes={filters.travellerTypes}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>

      <FilterPanelFilterGroup
        title={priceFilterTitle}
        canReset={!!filters.price?.min || !!filters.price?.max}
        onReset={onResetPrices}
      >
        <CruisePriceFilter
          filters={mappedFilters}
          onChange={onPriceFilterChange}
          cruisePrices={facets.cruisePrices}
        />
      </FilterPanelFilterGroup>
      {!shouldHideCruiseShips && <FilterPanelFilterGroup
        title={intl.formatMessage({
          id: 'SFDxc3kmdQ1Kc47YuFNr',
          defaultMessage: 'Cruise ships',
          description: 'Title for the cruise ships filter group in cruise search',
        })}
        canReset={!!filters.cruiseShips?.size}
        onReset={onFilterChange}
      >
        <CruiseShipFilter
          cruiseShips={filters.cruiseShips}
          shipNameFacets={facets.shipNames}
          onChange={onDelayedFilterChange}
        />
      </FilterPanelFilterGroup>}
    </>}
  </VerticalSpacer>
}

export default CruiseSearchFiltersInputs
